import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useToast } from "../lib/use-toast";
import { useCompareEntities, useImportEntities, useProcessChecklists, useChecklistDependencyImport, useChecklistImport, useAuthenticate, useProcessMigrationImport } from "../lib/api";
import { useWorkflowStore } from "../store/useWorkflowStore";
import type { ImportedChecklistItem, OntologyRecord } from "../store/useWorkflowStore";
import { useConnectedServices } from "../lib/api";
import { useAuthStore } from "../store/useAuthStore";

import { AppSidebar } from "../components/app-sidebar";
import { SiteHeader } from "../components/site-header";
import { SidebarInset, SidebarProvider } from "../components/ui/sidebar";
import WorkflowStates from "../components/workflow/WorkflowStates";
import WorkflowDetailsForm from "../components/workflow/WorkflowDetailsForm";
import WorkflowSourceForm from "../components/workflow/WorkflowSourceForm";
import WorkflowResults from "../components/workflow/WorkflowResults";
import TargetInstanceForm from "../components/workflow/TargetInstanceForm";

/**
 * WorkflowDetailPage component
 * 
 * Main page for workflow details and migration process
 */
const WorkflowDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  // Extract the workflow type from the URL path
  const workflowType = window.location.pathname.includes('/workflows/process/') ? 'process' : 'ontology';
  const { toast } = useToast();
  const { isAuthenticated } = useAuthStore();
  const [redirecting, setRedirecting] = useState(false);

  // Workflow store
  const {
    viewState,
    formData,
    currentWorkflow,
    setViewState,
    processChecklistResults,
    resetForm,
    getSelectedItems,
    selectedClient,
  } = useWorkflowStore();

  // Local state for loading
  const [isLoadingWorkflow] = useState(false);

  // Get authentication data for facility information (must be before handlers)
  const { data: authData } = useAuthenticate(selectedClient);

  // Fetch connected services
  const { refetch } = useConnectedServices();

  // Get the API hooks
  const { mutate: compareEntities } = useCompareEntities();
  const { mutate: importEntities } = useImportEntities();
  const { mutate: processChecklists } = useProcessChecklists();
  // Ontology migration uses checklist-dependency import, Process migration uses checklists import
  const { fetchDependencies, isLoading: isImportingDependencies } = useChecklistDependencyImport();
  const { importChecklists, isLoading: isImportingChecklists } = useChecklistImport();
  // Process migration final import
  const { processImport, isLoading: isProcessingImport } = useProcessMigrationImport();

  // Refetch connected services when authentication state changes
  useEffect(() => {
    if (isAuthenticated) {
      refetch();
    }
  }, [isAuthenticated, refetch]);

  // Check if we have the workflow in the store
  useEffect(() => {
    // If we have an ID but no current workflow, check if we need to redirect
    if (id && !currentWorkflow && !redirecting) {
      setRedirecting(true);
      toast({
        variant: "destructive",
        title: "Workflow Not Found",
        description: "The requested workflow could not be found.",
      });
      
      const timer = setTimeout(() => {
        navigate("/workflow");
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [id, currentWorkflow, navigate, toast, redirecting]);
  
  // Determine if we should show error state
  const shouldShowError = !currentWorkflow && !isLoadingWorkflow;

  // Handle source form completion
  const handleSourceFormComplete = async () => {
    const {
      selectedChecklists,
      selectedFacilityId,
      selectedServiceId
    } = useWorkflowStore.getState();

    if (selectedChecklists.length === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No checklists selected",
      });
      return;
    }

    if (!selectedFacilityId || !selectedServiceId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Facility and Client must be selected",
      });
      return;
    }

    // If we're in process migration, call /checklist-dependency to get ontologyRecords
    if (workflowType === 'process') {
      try {
        setViewState("loading");

        // Extract just the IDs from the selected checklists
        const checklistIds = selectedChecklists.map(checklist => checklist.id);

        // Prepare the API request parameters (without target params)
        const importParams = {
          serviceId: selectedServiceId.toString(),
          facilityId: selectedFacilityId,
          checklistIds: checklistIds,
          databaseName: formData["databaseName"] || "",
          hostId: formData["host"] || ""
        };

        console.log('Calling /checklist-dependency/import API for process migration with params:', importParams);

        // Call the checklist-dependency import API for process migration
        const data = await fetchDependencies(importParams);

        // Store ontologyRecords in workflow store
        if (data && typeof data === 'object' && 'ontologyRecords' in data) {
          useWorkflowStore.getState().processOntologyRecords(data as { ontologyRecords: OntologyRecord[] });
        }

        // Then go to target selection
        setViewState("target-selection");
      } catch (error) {
        console.error("Error calling /checklist-dependency for process migration:", error);

        toast({
          variant: "destructive",
          title: "Import Failed",
          description:
            error instanceof Error ? error.message : "Failed to fetch checklist dependencies",
        });

        setViewState("form");
      }
    } else {
      // For ontology migration, proceed directly to loading
      handleProcessImport();
    }
  };

  // Handle target form completion
  const handleTargetFormComplete = () => {
    if (workflowType === 'process') {
      // For process migration, skip compare API and directly show results
      // The ontologyRecords are already stored from the source form step
      setViewState("results");
    } else {
      // For ontology migration, call compare API
      handleCompareProcess();
    }
  };

  // Handle process comparison (for process migration after target selection)
  const handleCompareProcess = async () => {
    try {
      setViewState("loading");

      const {
        selectedChecklists,
        selectedFacilityId,
        selectedServiceId,
        targetServiceId,
        targetFacilityId,
        targetAuthorId,
        targetUseCaseId,
        currentWorkflow,
        ontologyRecords
      } = useWorkflowStore.getState();

      console.log(`Comparing checklists for migration - source facility: ${selectedFacilityId}, target facility: ${targetFacilityId}`);

      // Extract just the IDs from the selected checklists
      const checklistIds = selectedChecklists.map(checklist => checklist.id);

      // Get facility name from the authentication data
      const facilities = authData?.facilities || [];
      const selectedFacility = facilities.find(f => f.id === selectedFacilityId);
      const facilityName = selectedFacility?.name || "";

      // Prepare the API request parameters for comparison
      const compareParams: any = {
        // Source information
        databaseName: formData["databaseName"] || "",
        host: formData["host"] || "",
        serviceId: selectedServiceId ? selectedServiceId.toString() : "",
        facilityId: selectedFacilityId || "",
        facilityName: facilityName,
        checklistIds: checklistIds,

        // Workflow information
        code: currentWorkflow?.id || "",
        name: currentWorkflow?.name || "",
        description: currentWorkflow?.description || "",

        // Target information
        targetServiceId: targetServiceId ? targetServiceId.toString() : "",
        targetFacilityId: targetFacilityId || "",
        targetUseCaseId: targetUseCaseId || "",
        targetDatabaseName: formData["targetDatabaseName"] || "",
        targetHost: formData["targetHost"] || "",

        // Include import data from /import call
        importData: ontologyRecords || []
      };

      // Include target author if selected
      if (targetAuthorId) {
        compareParams.targetAuthorIds = [targetAuthorId];
      }

      console.log('Calling /compare API with params:', compareParams);

      // Call the compare API
      const data = await compareEntities(compareParams);

      // Check if the response has ontologyRecords
      if (data && typeof data === 'object' && 'ontologyRecords' in data) {
        // Process ontology records
        useWorkflowStore.getState().processOntologyRecords(data as { ontologyRecords: OntologyRecord[] });
      } else {
        // Fallback to old format for backward compatibility
        processChecklistResults(data as ImportedChecklistItem[]);
      }

      setViewState("results");
    } catch (error) {
      console.error("Error comparing checklists:", error);

      toast({
        variant: "destructive",
        title: "Comparison Failed",
        description:
          error instanceof Error ? error.message : "Failed to compare checklists",
      });

      setViewState("target-selection");
    }
  };

  // Handle process import (for ontology migration - called after source form)
  const handleProcessImport = async () => {
    try {
      setViewState("loading");

      const {
        selectedChecklists,
        selectedFacilityId,
        selectedServiceId,
        targetServiceId,
        targetFacilityId,
        targetAuthorId,
        targetUseCaseId
      } = useWorkflowStore.getState();

      console.log(`Importing checklist dependencies for facility: ${selectedFacilityId}, serviceId: ${selectedServiceId}`);

      // Extract just the IDs from the selected checklists
      const checklistIds = selectedChecklists.map(checklist => checklist.id);

      // Prepare the API request parameters
      const importParams: any = {
        serviceId: selectedServiceId ? selectedServiceId.toString() : "",
        facilityId: selectedFacilityId || "",
        checklistIds: checklistIds,
        databaseName: formData["databaseName"] || "",
        hostId: formData["host"] || ""
      };

      // Add target parameters for process migration
      if (workflowType === 'process' && targetServiceId && targetFacilityId) {
        importParams.targetServiceId = targetServiceId.toString();
        importParams.targetFacilityId = targetFacilityId;

        // Include target author if selected
        if (targetAuthorId) {
          importParams.targetAuthorIds = [targetAuthorId];
        }

        // Include target use case if selected
        if (targetUseCaseId) {
          importParams.targetUseCaseId = targetUseCaseId;
        }
      }

      // Call the appropriate API based on workflow type
      // Ontology migration → /api/checklist-dependency/import
      // Process migration → /api/checklists/import
      const data = workflowType === 'process'
        ? await importChecklists(importParams)
        : await fetchDependencies(importParams);

      // Check if the response has ontologyRecords
      if (data && typeof data === 'object' && 'ontologyRecords' in data) {
        // Process ontology records
        useWorkflowStore.getState().processOntologyRecords(data as { ontologyRecords: OntologyRecord[] });
      } else {
        // Fallback to old format for backward compatibility
        processChecklistResults(data as ImportedChecklistItem[]);
      }

      setViewState("results");
    } catch (error) {
      console.error("Error importing checklist dependencies:", error);

      toast({
        variant: "destructive",
        title: "Import Failed",
        description:
          error instanceof Error ? error.message : "Failed to import checklist dependencies",
      });

      setViewState("form");
    }
  };

  // Handle back button click
  const handleBack = () => {
    resetForm();
    setViewState("form");
  };

  // Handle reset and navigate to home
  const handleReset = () => {
    resetForm();
    navigate("/workflow");
  };

  // Handle confirm button click - calls /compare for ontology, /checklists/import for process
  const handleConfirm = async () => {
    try {
      // Get the appropriate selected data based on what we have
      const {
        ontologyRecords,
        getSelectedOntologyRecords,
        getSelectedItems,
        currentWorkflow,
        selectedFacilityId,
        selectedServiceId,
        selectedChecklists,
        targetServiceId,
        targetFacilityId,
        targetUseCaseId,
        targetAuthorId
      } = useWorkflowStore.getState();

      // Extract just the IDs from the selected checklists
      const checklistIds = selectedChecklists.map(checklist => checklist.id);

      setViewState("migrating");

      if (workflowType === 'process') {
        // Process migration: Call /api/checklists/import with GET query params
        console.log('Calling /checklists/import API for process migration');

        await processImport({
          checklistIds: checklistIds,
          serviceId: selectedServiceId ? selectedServiceId.toString() : "",
          facilityId: selectedFacilityId || "",
          databaseName: formData["databaseName"] || "",
          hostId: formData["host"] || "",
          targetServiceId: targetServiceId ? targetServiceId.toString() : "",
          targetFacilityId: targetFacilityId || "",
          targetAuthorIds: targetAuthorId || undefined,
          targetUseCaseId: targetUseCaseId || undefined,
          targetChecklistId: useWorkflowStore.getState().targetChecklistId || undefined
        });
      } else {
        // Ontology migration: Call /api/object-type-aggregation/compare
        const hasOntologyRecords = ontologyRecords.length > 0;

        // Use ontology records if available, otherwise use checklist items
        const selectedData = hasOntologyRecords
          ? getSelectedOntologyRecords()
          : getSelectedItems();

        if (selectedData.length === 0) {
          toast({
            variant: "destructive",
            title: "No Items Selected",
            description: "Please select at least one item to migrate",
          });
          setViewState("results");
          return;
        }

        // Get facility name from the authentication data
        const facilities = authData?.facilities || [];
        const selectedFacility = facilities.find(f => f.id === selectedFacilityId);
        const facilityName = selectedFacility?.name || "";

        // Create the import data object for ontology migration
        const importData: any = {
          // Source information
          databaseName: formData["databaseName"] || "",
          host: formData["host"] || "",
          serviceId: selectedServiceId ? selectedServiceId.toString() : "",
          facilityId: selectedFacilityId || "",
          facilityName: facilityName,
          checklistIds: checklistIds,

          // Workflow information
          code: currentWorkflow?.id || "",
          name: currentWorkflow?.name || "",
          description: currentWorkflow?.description || "",

          // Selected items to import
          importData: selectedData,

          // Target information
          targetServiceId: targetServiceId ? targetServiceId.toString() : "",
          targetFacilityId: targetFacilityId || "",
          targetUseCaseId: targetUseCaseId || "",
          targetDatabaseName: formData["targetDatabaseName"] || "",
          targetHost: formData["targetHost"] || ""
        };

        // Include target author if selected
        if (targetAuthorId) {
          importData.targetAuthorIds = [targetAuthorId];
        }

        console.log('Calling /compare API for ontology migration:', {
          ...importData,
          importData: `[${selectedData.length} items]` // Log count instead of full array
        });

        await compareEntities(importData);
      }

      toast({
        title: "Import Successful",
        description: "All selected entities have been imported successfully",
      });

      setViewState("success");
    } catch (error) {
      console.error("Error importing entities:", error);

      toast({
        variant: "destructive",
        title: "Import Failed",
        description:
          error instanceof Error ? error.message : "Failed to import entities",
      });

      setViewState("results");
    }
  };

  return (
    <SidebarProvider
      style={{
        "--sidebar-width": "16rem",
        "--header-height": "3rem",
      } as React.CSSProperties}
      className="min-h-screen"
    >
      <AppSidebar variant="inset" />
      <SidebarInset className="pt-0">
        <SiteHeader />
        <div className="flex flex-1 flex-col p-4">
          {isLoadingWorkflow && <WorkflowStates.Loading />}
          
          {shouldShowError && <WorkflowStates.Error />}
          
          {!isLoadingWorkflow && currentWorkflow && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground">
                  {workflowType === 'process' ? 'Process Migration' : 'Ontology Migration'}
                </h1>
              </div>
              <div>
                {viewState === "form" && (
                  <>
                    <WorkflowDetailsForm />
                    <WorkflowSourceForm onNext={handleSourceFormComplete} />
                  </>
                )}

                {viewState === "target-selection" && (
                  <TargetInstanceForm onNext={handleTargetFormComplete} />
                )}

                {viewState === "loading" && <WorkflowStates.Processing />}

                {viewState === "results" && (
                  <WorkflowResults onBack={handleBack} onConfirm={handleConfirm} workflowType={workflowType} />
                )}

                {viewState === "migrating" && <WorkflowStates.Migrating />}

                {viewState === "success" && (
                  <WorkflowStates.Success onReset={handleReset} />
                )}
              </div>
            </>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default WorkflowDetailPage;